import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build'; // For restoration/benefits
import HealingIcon from '@mui/icons-material/Healing'; // For when fillings are needed
import PaletteIcon from '@mui/icons-material/Palette'; // For types of materials (aesthetics)
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload'; // For process
import SpaIcon from '@mui/icons-material/Spa'; // For home care / why choose us
import StarIcon from '@mui/icons-material/Star'; // For why choose us
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const DentalFillings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Effectively stops the progression of tooth decay and cavities.',
    'Restores the tooth\'s natural strength, form, and function.',
    'Prevents further damage that could lead to more complex treatments.',
    'Modern tooth-colored fillings offer a virtually invisible restoration.',
    'Relieves tooth sensitivity and pain caused by decay.',
    'Often a quick and minimally invasive procedure with immediate results.'
  ];

  const process = [
    {
      title: '1. Numbing the Area & Ensuring Comfort',
      description: 'We begin by applying a local anesthetic to numb the affected tooth and surrounding area, ensuring a comfortable and pain-free experience.'
    },
    {
      title: '2. Gentle Removal of Decay',
      description: 'The decayed portion of the tooth is carefully and thoroughly removed using precise dental instruments. We aim to preserve as much healthy tooth structure as possible.'
    },
    {
      title: '3. Preparing & Cleaning the Cavity',
      description: 'Once the decay is removed, the cavity is cleaned, disinfected, and meticulously prepared for the filling material.'
    },
    {
      title: '4. Placing the Filling Material',
      description: 'For tooth-colored composite fillings, the material is applied in layers. Each layer is hardened (cured) using a special dental light before the next is applied.'
    },
    {
        title: '5. Shaping & Polishing for a Natural Finish',
        description: 'After the filling is placed, it\'s carefully shaped to match the natural contours of your tooth and polished to a smooth, aesthetically pleasing finish, ensuring a comfortable bite.'
    }
  ];

  const whenNeeded = [
    'You experience tooth sensitivity to hot, cold, or sweet foods/drinks.',
    'There\'s a visible hole, pit, or dark spot on your tooth.',
    'Food frequently gets trapped between certain teeth.',
    'You have a minor chip or fracture in a tooth.',
    'Your dentist identifies a cavity during a routine exam or through X-rays.',
    'An old filling needs to be replaced due to wear, chipping, or leakage.'
  ];

  const typesOfFillings = [
    {
      name: 'Composite Resin (Tooth-Colored)',
      desc: 'Our preferred choice for most fillings. They bond directly to the tooth, providing good support and a natural appearance. Available in various shades to match your teeth perfectly.',
      icon: <PaletteIcon sx={{ color: 'primary.main' }}/>
    },
    {
      name: 'Ceramic/Porcelain Fillings (Inlays/Onlays)',
      desc: 'Highly aesthetic and durable, these are often used for larger cavities. They are custom-made in a lab and then bonded to the tooth.',
      icon: <StarIcon sx={{ color: 'secondary.main' }}/>
    },
    // Add other types if relevant, e.g., Glass Ionomer
  ];

  const caringTips = [
    'Maintain excellent oral hygiene: Brush twice daily with fluoride toothpaste and floss at least once a day.',
    'Attend regular dental check-ups and cleanings as recommended by your dentist.',
    'Avoid biting down on extremely hard foods or objects (like ice or pens) with filled teeth.',
    'If you clench or grind your teeth, consider discussing a nightguard with your dentist.',
    'Report any unusual sensitivity, chipping, or discomfort around your fillings promptly.'
  ];
  
  const faqs = [
    {
      question: 'Is getting a dental filling painful?',
      answer: 'No, the procedure is generally not painful. We use local anesthesia to numb the area effectively. You might feel some pressure, but not pain. Some mild sensitivity after the numbness wears off is normal but usually subsides quickly.'
    },
    {
      question: 'How long does it take to get a dental filling?',
      answer: 'A typical dental filling procedure can take anywhere from 20 minutes to an hour, depending on the size and location of the cavity and the type of filling material used.'
    },
    {
      question: 'How long do dental fillings last?',
      answer: 'The lifespan of a filling depends on the material used, the size of the filling, its location, and your oral hygiene habits. Composite fillings can last 5-7 years or longer.'
    },
    {
        question: 'Can I eat or drink immediately after getting a filling?',
        answer: 'For composite (tooth-colored) fillings, you can typically eat and drink right away as they harden immediately. It\'s always best to avoid very hot or sticky foods initially if your mouth is still numb.'
    },
    {
        question: 'What should I do if my filling feels high or uncomfortable?',
        answer: 'If your bite feels off or the filling feels high after the numbness wears off, contact our office. We can easily adjust the filling to ensure a comfortable and proper bite.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dental Fillings | Cavity Treatment | Pacifica Dental</title>
        <meta name="description" content="Restore decayed or damaged teeth with durable and natural-looking dental fillings at Pacifica Dental. Learn about our composite fillings and cavity treatment process." />
        <meta name="keywords" content="dental fillings, tooth fillings, cavity treatment, composite fillings, white fillings, tooth restoration, [Your City] dentist, restorative dentistry, cavity repair" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Restore Your Smile with Modern Dental Fillings
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Effective Solutions for Cavities and Tooth Decay
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              At Pacifica Dental, we provide state-of-the-art dental fillings to repair teeth affected by decay, cracks, or fractures. Our goal is to restore your tooth's health and function while maintaining a natural appearance. Using advanced materials, especially tooth-colored composites, we can seamlessly repair cavities, often in a single visit, helping you smile with confidence again.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Advantages of Our Dental Fillings
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                          <BuildIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    The Dental Filling Procedure: Step-by-Step
                  </Typography>
                  {process.map((step, index) => (
                    <Box key={index} sx={{ mb: index === process.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                         <AssuredWorkloadIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* When are Fillings Needed? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Do You Need a Dental Filling?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#2c3e50' }}>
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  You might need a dental filling if you notice any of the following signs or symptoms:
                </Typography>
                <Grid container spacing={1}>
                    {whenNeeded.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        <HealingIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Types of Filling Materials Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Filling Materials We Offer
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    {typesOfFillings.map((type, index) => (
                        <Grid item xs={12} sm={6} md={isMobile ? 12 : (typesOfFillings.length === 3 ? 4 : 3)} key={index}> {/* Adjust md for centering if 3 items */}
                            <Paper elevation={2} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                                <Box sx={{display: 'flex', alignItems: 'center', mb:1}}>
                                    {type.icon}
                                    <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize:'1.1rem', color: 'secondary.dark', ml:1}}>
                                        {type.name}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{flexGrow: 1, fontSize: '0.95rem'}}>
                                    {type.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Caring for Your Fillings Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Protecting Your Dental Fillings for Lasting Results
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}>
                    <List>
                        {caringTips.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <CheckCircleOutlineIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />


            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Common Questions About Dental Fillings
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Us Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Us for Your Dental Fillings?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Aesthetic, Tooth-Colored Materials", text: "We prioritize composite fillings that blend seamlessly with your natural teeth for a beautiful, unnoticeable restoration.", icon: <PaletteIcon color="primary" /> },
                      { title: "Minimally Invasive Techniques", text: "Our approach focuses on preserving as much healthy tooth structure as possible, ensuring gentle and conservative treatment.", icon: <HealingIcon color="primary" /> },
                      { title: "Focus on Comfort & Gentle Care", text: "We strive to make your filling procedure as comfortable and stress-free as possible, with effective anesthesia and a caring team.", icon: <SpaIcon color="primary" /> },
                      { title: "Durable & Long-Lasting Results", text: "We use high-quality materials and meticulous techniques to ensure your fillings are strong, durable, and reliable.", icon: <StarIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Appointment
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call us at <Link to="tel:(408) 869-0998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> if you have any questions.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default DentalFillings;